import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  OutlinedInput,
  ListItemText,
  FormControlLabel,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getLanguageText } from '../../utils/multilanguageUtils';
import VideoGenerationStatus from '../backend/buttonApp/VideoGenerationStatus';
import { useMappedInSettings } from '../../hooks/useMappedInSettings';
import { useMappedInDirections } from '../../hooks/useMappedInDirections';
import { removeSSMLTags, enhanceDirectionsWithAI, getConcatenatedDirectionsText } from '../../services/mappedInDirectionsService';
import GetAppIcon from '@mui/icons-material/GetApp';

// Default fallback URL - will be replaced by dynamic mapLink from settings
const DEFAULT_BASE_URL = 'https://web.mappedin.com/lga-laguardia/laguardia-airport.html';
const DEFAULT_FROM_NODE = 'LOC-1IP30ZO0';

const buildMapLink = (externalId, mapLink, startLocation) => {
  if (!externalId) return '';
  const baseUrl = mapLink || DEFAULT_BASE_URL;
  const fromNode = startLocation || DEFAULT_FROM_NODE;
  const base = baseUrl.split('#')[0].replace(/\/$/, '');
  return `${base}#/directions?to=${externalId}&from=${fromNode}&textDirectionsVisible=true&instruction=0`;
};

// Language display names
const LANG_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
  pt: 'Portuguese', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ar: 'Arabic', hi: 'Hindi', tr: 'Turkish', nl: 'Dutch', sv: 'Swedish',
  pl: 'Polish', th: 'Thai', vi: 'Vietnamese', id: 'Indonesian',
};
const getLangName = (code) => LANG_NAMES[code] || code?.toUpperCase() || '';

// Page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/* ══════════════════════════════════════════════════════════
   CSV Export Helper
   ══════════════════════════════════════════════════════════ */
const escapeCSV = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Helper to strip SSML break tags from text
const stripBreakTags = (text) => {
  if (!text) return '';
  return text
    // Remove SSML break tags
    .replace(/<break\s+time="[^"]*"\s*\/>/g, ' ')
    // Remove any other SSML tags
    .replace(/<[^>]*>/g, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim whitespace
    .trim();
};

// Helper to format text as continuous paragraph (removes line breaks but keeps SSML tags)
const formatAsContinuousParagraph = (text) => {
  if (!text) return '';
  return text
    // Replace newlines with spaces
    .replace(/\n+/g, ' ')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim whitespace
    .trim();
};

const exportToCSV = (locations, shouldStripBreakTags = false) => {
  // Collect all languages across locations
  const allLangs = new Set();
  locations.forEach((loc) => {
    if (loc.textDirectionsMultiLang && typeof loc.textDirectionsMultiLang === 'object') {
      Object.keys(loc.textDirectionsMultiLang).forEach((l) => allLangs.add(l));
    }
    if (loc.avatarVideosMultiLang && typeof loc.avatarVideosMultiLang === 'object') {
      Object.keys(loc.avatarVideosMultiLang).forEach((l) => allLangs.add(l));
    }
  });
  const langs = Array.from(allLangs).sort();

  // Header row - matching table structure exactly
  const headers = [
    'S. No.',
    'Location Name',
    'Map ID',
    'Category',
    'Tags',
    ...langs.map((l) => `Avatar File (${getLangName(l)})`),
    '# Steps',
    ...langs.map((l) => `Duration (${getLangName(l)})`),
    ...langs.map((l) => `Caption File (${getLangName(l)})`),
    'Delay Time (s)',
    ...langs.map((l) => `Text Directions (${getLangName(l)})`),
  ];

  const rows = locations.map((loc, index) => {
    const locName = getLanguageText(loc.name, 'en');
    const spaceName = loc.spaceName && loc.spaceName.trim() !== '' ? loc.spaceName : locName;
    const categoryName = loc.categoryName || '';
    
    // Get tag names
    const tagNames = (loc.tags || [])
      .map(tag => {
        if (typeof tag === 'object') {
          return getLanguageText(tag.name, 'en') || 'Unnamed Tag';
        }
        return tag;
      })
      .join(', ');

    return [
      escapeCSV(index + 1),
      escapeCSV(spaceName || ''),
      escapeCSV(loc.externalId || ''),
      escapeCSV(categoryName),
      escapeCSV(tagNames),
      ...langs.map((l) => escapeCSV(loc.avatarVideosMultiLang?.[l]?.fileName || '')),
      escapeCSV(loc.totalSteps || 0),
      ...langs.map((l) => {
        const dur = loc.avatarVideosMultiLang?.[l]?.videoDuration;
        return escapeCSV(dur ? `${Math.round(dur / 1000)}s` : '');
      }),
      ...langs.map((l) => {
        const captionFile = loc.avatarVideosMultiLang?.[l]?.captionFile;
        if (!captionFile) return escapeCSV('');
        const fileName = captionFile.split('/').pop() || `${l}.vtt`;
        return escapeCSV(fileName);
      }),
      escapeCSV(loc.mapDelayTime ?? ''),
      ...langs.map((l) => {
        const textDirection = loc.textDirectionsMultiLang?.[l] || '';
        return escapeCSV(shouldStripBreakTags ? stripBreakTags(textDirection) : textDirection);
      }),
    ].join(',');
  });

  const csv = [headers.map(escapeCSV).join(','), ...rows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  // Generate timestamp with date and time
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS
  const suffix = shouldStripBreakTags ? '_no_break_tags' : '';

  a.download = `all_locations_${dateStr}_${timeStr}${suffix}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ══════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════ */
const AllLocationsTable = ({ buttonColor = '#1a1a2e' }) => {
  const { userId } = useParams();
  
  // Fetch MappedIn settings for dynamic map URL and start location
  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editState, setEditState] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // View/Edit modal state
  const [viewModal, setViewModal] = useState({ open: false, locId: null, lang: null, text: '', locationName: '' });
  // Schedule modal state
  const [scheduleModal, setScheduleModal] = useState({ open: false, pendingUpdates: [], saveOnlyUpdates: [], locationName: '', lang: null });
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  // Global save state
  const [savingAll, setSavingAll] = useState(false);
  const [videoStatusModalOpen, setVideoStatusModalOpen] = useState(false);
  const originalEditStateRef = useRef({});

  // Selection state for bulk delete
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Single delete state
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [deletingSingle, setDeletingSingle] = useState(false);

  // ── Feature 1: Category multi-select ──
  const [allCategories, setAllCategories] = useState([]);
  const [categoryEditState, setCategoryEditState] = useState({}); // { locId: [catId1, catId2] }
  const originalCategoryEditRef = useRef({});

  // ── Feature 1b: Tags multi-select ──
  const [allTags, setAllTags] = useState([]);
  const [tagEditState, setTagEditState] = useState({}); // { locId: [tagId1, tagId2] }
  const originalTagEditRef = useRef({});

  // ── Feature 2: Caption file popup ──
  const [captionModal, setCaptionModal] = useState({ open: false, locId: null, lang: null, content: '', locationName: '', captionUrl: '', loading: false, saving: false });

  // ── Feature 3: Breaktime tag toggle ──
  const [hideBreakTags, setHideBreakTags] = useState(false);

  // ── Feature 4: Get Text Directions ──
  const { fetchDirections } = useMappedInDirections(userId);
  const [fetchingDirections, setFetchingDirections] = useState({});

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Search
  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);

  /* ── Fetch Categories ────────────────────────────────────── */
  const fetchCategories = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`/api/wayfinder/get-categories/${userId}`);
      if (response.data.success) {
        const categories = response.data.data || [];
        // Remove duplicates based on _id - convert to string for comparison
        const uniqueCategories = categories.filter((cat, index, self) => {
          const catId = (cat._id || cat.id)?.toString();
          return index === self.findIndex((c) => (c._id || c.id)?.toString() === catId);
        });
        console.log(`Loaded ${categories.length} categories, filtered to ${uniqueCategories.length} unique categories`);
        setAllCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [userId]);

  /* ── Fetch Tags ──────────────────────────────────────────── */
  const fetchTags = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`/api/tags/${userId}`);
      if (response.data.success) {
        setAllTags(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, [userId]);

  /* ── Fetch Locations with Server-Side Pagination ────────── */
  const [totalCount, setTotalCount] = useState(0);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const isInitialLoad = useRef(true);

  const fetchLocations = useCallback(async (currentPage = page, currentPageSize = pageSize, searchQuery = search) => {
    if (!userId) return;
    
    // Only show loading spinner on initial load, not on search/pagination
    if (isInitialLoad.current) {
      setLoading(true);
    }
    
    try {
      const response = await axios.get(`/api/wayfinder/get-all-locations-with-categories/${userId}`, {
        params: {
          page: currentPage + 1, // Backend expects 1-indexed pages
          limit: currentPageSize,
          search: searchQuery.trim() || undefined,
        }
      });
      
      if (response.data.success) {
        const locs = response.data.data || [];
        const total = response.data.total || 0;
        
        setLocations(locs);
        setTotalCount(total);
        
        const initial = {};
        const catInitial = {};
        const tagInitial = {};
        locs.forEach((loc) => {
          initial[loc._id] = {
            mapDelayTime: loc.mapDelayTime ?? 2,
            textDirectionsMultiLang: loc.textDirectionsMultiLang
              ? { ...loc.textDirectionsMultiLang }
              : { en: '' },
          };
          catInitial[loc._id] = loc.categoryIds || [];
          // Extract tag IDs from tags array (handle both object and string formats)
          tagInitial[loc._id] = (loc.tags || []).map(tag => typeof tag === 'object' ? tag._id : tag);
        });
        setEditState(initial);
        setCategoryEditState(catInitial);
        setTagEditState(tagInitial);
        originalEditStateRef.current = JSON.parse(JSON.stringify(initial));
        originalCategoryEditRef.current = JSON.parse(JSON.stringify(catInitial));
        originalTagEditRef.current = JSON.parse(JSON.stringify(tagInitial));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSnackbar({ open: true, message: 'Failed to load locations', severity: 'error' });
    } finally {
      if (isInitialLoad.current) {
        setLoading(false);
        isInitialLoad.current = false;
      }
    }
  }, [userId]); // Only depend on userId

  // Initial load
  useEffect(() => { 
    fetchLocations(0, 50, ''); // Use explicit initial values
    fetchCategories(); 
    fetchTags(); 
  }, [fetchCategories, fetchTags, fetchLocations]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      setPage(0); // Reset to first page on search
      fetchLocations(0, pageSize, search);
    }, 500); // 500ms debounce

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageSize]); // Don't include fetchLocations here

  // Page change effect
  useEffect(() => {
    fetchLocations(page, pageSize, search);
  }, [page]); // Only trigger on page change, not pageSize or search

  /* ── Pagination derived values ──────────────────────────── */
  const totalPages = Math.ceil(totalCount / pageSize);
  const paged = locations; // Already paginated from server

  /* ── Selection handlers ──────────────────────────────────── */
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLocations(new Set(paged.map(loc => loc._id)));
    } else {
      setSelectedLocations(new Set());
    }
  };

  const handleSelectLocation = (locId) => {
    setSelectedLocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locId)) {
        newSet.delete(locId);
      } else {
        newSet.add(locId);
      }
      return newSet;
    });
  };

  const isAllSelected = paged.length > 0 && paged.every(loc => selectedLocations.has(loc._id));
  const isSomeSelected = paged.some(loc => selectedLocations.has(loc._id)) && !isAllSelected;

  /* ── Delete handlers ─────────────────────────────────────── */
  const handleDeleteSelected = async () => {
    if (selectedLocations.size === 0) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const response = await axios.post('/api/locations/bulk-delete', {
        locationIds: Array.from(selectedLocations),
        userId,
      });

      if (response.data.success) {
        const { deleted, notFound, unauthorized, errors } = response.data.data;
        
        let message = `Deleted ${deleted.length} location(s) successfully`;
        if (notFound.length > 0) message += `, ${notFound.length} not found`;
        if (unauthorized.length > 0) message += `, ${unauthorized.length} unauthorized`;
        if (errors.length > 0) message += `, ${errors.length} failed`;

        setSnackbar({
          open: true,
          message,
          severity: errors.length > 0 ? 'warning' : 'success',
        });

        // Clear selection and refresh
        setSelectedLocations(new Set());
        await fetchLocations(page, pageSize, search);
      }
    } catch (error) {
      console.error('Error deleting locations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete locations',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  /* ── Single Delete handlers ──────────────────────────────── */
  const handleDeleteSingle = (location) => {
    setLocationToDelete(location);
    setSingleDeleteConfirmOpen(true);
  };

  const confirmSingleDelete = async () => {
    if (!locationToDelete) return;
    
    setDeletingSingle(true);
    try {
      const response = await axios.delete(`/api/locations/${locationToDelete._id}`, {
        data: { userId }
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Location deleted successfully',
          severity: 'success',
        });
        await fetchLocations(page, pageSize, search);
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete location',
        severity: 'error',
      });
    } finally {
      setDeletingSingle(false);
      setSingleDeleteConfirmOpen(false);
      setLocationToDelete(null);
    }
  };

  /* ── Edit handlers ──────────────────────────────────────── */
  const handleMapDelayChange = (locId, value) =>
    setEditState((p) => ({ ...p, [locId]: { ...p[locId], mapDelayTime: value === '' ? '' : Number(value) } }));

  const handleTextDirectionChange = (locId, lang, value) =>
    setEditState((p) => ({
      ...p,
      [locId]: { ...p[locId], textDirectionsMultiLang: { ...(p[locId]?.textDirectionsMultiLang || {}), [lang]: value } },
    }));

  const executeSave = async (scriptUpdatesLocIds, mode, saveOnlyLocIds = [], scheduledDateTime = null) => {
    setSavingAll(true);
    let successCount = 0;
    let errorCount = 0;
    let videoTriggered = false;

    const allLocIds = [...scriptUpdatesLocIds, ...saveOnlyLocIds];

    for (const locId of allLocIds) {
      setSavingRows((p) => ({ ...p, [locId]: true }));
      const edits = editState[locId];
      if (!edits) {
        setSavingRows((p) => ({ ...p, [locId]: false }));
        continue;
      }

      const orig = originalEditStateRef.current[locId] || {};
      const location = locations.find(l => l._id === locId);

      try {
        const hasScriptUpdates = scriptUpdatesLocIds.includes(locId);
        let changedLangs = [];

        // Check for changed languages in current session
        if (edits.textDirectionsMultiLang) {
          changedLangs = Object.keys(edits.textDirectionsMultiLang).filter(
            lang => edits.textDirectionsMultiLang[lang] !== orig.textDirectionsMultiLang?.[lang]
          );
        }

        // If location has textDirectionsChanged flag but no session changes,
        // regenerate all languages that have text directions
        if (hasScriptUpdates && changedLangs.length === 0 && location?.textDirectionsChanged === true) {
          console.log(`[Force Regenerate] Location ${locId} has textDirectionsChanged flag, regenerating all languages`);
          changedLangs = Object.keys(edits.textDirectionsMultiLang || {}).filter(
            lang => (edits.textDirectionsMultiLang[lang] || '').trim().length > 0
          );
        }

        if (hasScriptUpdates && changedLangs.length > 0 && mode !== 'saveScriptOnly') {
          for (const lang of changedLangs) {
            if (mode === 'generateNow') {
              // Use force-regenerate endpoint to delete old videos and create new ones
              console.log(`[Force Regenerate] Generating video for location ${locId}, language: ${lang}`);
              await axios.put(`/api/wayfinder/locations/${locId}/force-regenerate-video`, {
                textDirections: edits.textDirectionsMultiLang[lang],
                currentLanguage: lang,
              });
              videoTriggered = true;
            } else if (mode === 'scheduleLater' && scheduledDateTime) {
              await axios.post(`/api/wayfinder/locations/${locId}/schedule-video-generation`, {
                textDirections: edits.textDirectionsMultiLang[lang],
                language: lang,
                scheduledDateTime: scheduledDateTime,
              });
            }
          }
          if (edits.mapDelayTime !== orig.mapDelayTime) {
            await axios.put(`/api/wayfinder/locations/${locId}`, {
              mapDelayTime: edits.mapDelayTime,
            });
          }
        } else {
          // Check if text directions actually changed
          let textChanged = false;
          if (edits.textDirectionsMultiLang && orig.textDirectionsMultiLang) {
            const allLangs = new Set([
              ...Object.keys(edits.textDirectionsMultiLang),
              ...Object.keys(orig.textDirectionsMultiLang)
            ]);
            
            for (const lang of allLangs) {
              const oldText = (orig.textDirectionsMultiLang[lang] || '').trim();
              const newText = (edits.textDirectionsMultiLang[lang] || '').trim();
              if (oldText !== newText) {
                textChanged = true;
                console.log(`[TextChanged] Language ${lang}: ${oldText.length} → ${newText.length} chars`);
                break;
              }
            }
          }
          
          await axios.put(`/api/wayfinder/locations/${locId}`, {
            mapDelayTime: edits.mapDelayTime,
            textDirectionsMultiLang: edits.textDirectionsMultiLang,
            // Set flag based on whether text actually changed
            textDirectionsChanged: textChanged,
          });
        }
        successCount++;
      } catch (e) {
        console.error(`[Error] Failed to save location ${locId}:`, e);
        errorCount++;
      } finally {
        setSavingRows((p) => ({ ...p, [locId]: false }));
      }

      // Also save category changes if any
      try {
        const curCats = categoryEditState[locId] || [];
        const origCats = originalCategoryEditRef.current[locId] || [];
        if (JSON.stringify(curCats.sort()) !== JSON.stringify(origCats.sort())) {
          await axios.put(`/api/wayfinder/locations/${locId}/update-categories`, { categoryIds: curCats });
        }
      } catch (e) {
        console.error('Failed to save categories for', locId, e);
      }

      // Also save tag changes if any
      try {
        const curTags = tagEditState[locId] || [];
        const origTags = originalTagEditRef.current[locId] || [];
        if (JSON.stringify(curTags.sort()) !== JSON.stringify(origTags.sort())) {
          await axios.post(`/api/tags/${userId}/location/${locId}/assign`, { tagIds: curTags });
        }
      } catch (e) {
        console.error('Failed to save tags for', locId, e);
      }
    }

    setSnackbar({
      open: true,
      message: `Saved ${successCount} location(s)${errorCount ? `, ${errorCount} failed` : ''}`,
      severity: errorCount ? 'warning' : 'success',
    });
    
    // Refresh locations to get updated data with current pagination settings
    await fetchLocations(page, pageSize, search);
    
    // Update original refs to match current state after successful save
    originalEditStateRef.current = JSON.parse(JSON.stringify(editState));
    originalCategoryEditRef.current = JSON.parse(JSON.stringify(categoryEditState));
    originalTagEditRef.current = JSON.parse(JSON.stringify(tagEditState));
    
    setSavingAll(false);

    if (videoTriggered) {
      setVideoStatusModalOpen(true);
    }
  };

  const handleSubmitRow = async (locId) => {
    const edits = editState[locId];
    if (!edits) return;

    const orig = originalEditStateRef.current[locId];
    let hasScriptChanges = false;

    if (orig && edits.textDirectionsMultiLang) {
      const changedLangs = Object.keys(edits.textDirectionsMultiLang).filter(
        lang => edits.textDirectionsMultiLang[lang] !== orig.textDirectionsMultiLang?.[lang]
      );
      if (changedLangs.length > 0) hasScriptChanges = true;
    }

    if (hasScriptChanges) {
      const loc = locations.find(l => l._id === locId);
      
      // Auto-fill with current date and time + 2 minutes
      const now = new Date();
      now.setMinutes(now.getMinutes() + 2); // Add 2 minutes
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().slice(0, 5); // HH:MM
      setScheduleDate(dateStr);
      setScheduleTime(timeStr);
      
      setScheduleModal({
        open: true,
        pendingUpdates: [locId],
        saveOnlyUpdates: [],
        locationName: loc ? getLanguageText(loc.name, 'en') : '',
        lang: null
      });
    } else {
      await executeSave([], 'saveScriptOnly', [locId]);
    }
  };

  /* ── Handle Generate Video button (opens schedule modal) ──── */
  const handleGenerateVideoClick = (locId) => {
    const loc = locations.find(l => l._id === locId);
    
    // Auto-fill with current date and time + 2 minutes
    const now = new Date();
    now.setMinutes(now.getMinutes() + 2);
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0, 5);
    setScheduleDate(dateStr);
    setScheduleTime(timeStr);
    
    setScheduleModal({
      open: true,
      pendingUpdates: [locId],
      saveOnlyUpdates: [],
      locationName: loc ? getLanguageText(loc.name, 'en') : '',
      lang: null
    });
  };

  /* ── Handle Submit Only button (saves without video generation) ──── */
  const handleSubmitOnlyClick = async (locId) => {
    await executeSave([], 'saveScriptOnly', [locId]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied!', severity: 'info' });
  };

  /* ── View/Edit Modal handlers ────────────────────────────── */
  const openViewModal = (locId, lang, locationName) => {
    const text = editState[locId]?.textDirectionsMultiLang?.[lang] ?? '';
    setViewModal({ open: true, locId, lang, text, locationName });
  };

  const handleViewModalSubmit = () => {
    handleTextDirectionChange(viewModal.locId, viewModal.lang, viewModal.text);
    setViewModal((m) => ({ ...m, open: false }));
    const loc = locations.find((l) => l._id === viewModal.locId);
    const locName = loc ? getLanguageText(loc.name, 'en') : '';
    
    // Auto-fill with current date and time + 2 minutes
    const now = new Date();
    now.setMinutes(now.getMinutes() + 2); // Add 2 minutes
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().slice(0, 5); // HH:MM
    setScheduleDate(dateStr);
    setScheduleTime(timeStr);
    
    setScheduleModal({
      open: true,
      pendingUpdates: [viewModal.locId],
      saveOnlyUpdates: [],
      locationName: locName,
      lang: viewModal.lang
    });
  };

  /* ── Schedule/Generate Modal handlers ────────────────────── */
  const handleGenerateNow = async () => {
    setScheduleModal((m) => ({ ...m, open: false }));
    await executeSave(scheduleModal.pendingUpdates || [], 'generateNow', scheduleModal.saveOnlyUpdates || []);
  };

  const handleScheduleLater = async () => {
    if (!scheduleDate || !scheduleTime) {
      setSnackbar({ open: true, message: 'Please select date and time', severity: 'error' });
      return;
    }
    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
    setScheduleModal((m) => ({ ...m, open: false }));
    await executeSave(scheduleModal.pendingUpdates || [], 'scheduleLater', scheduleModal.saveOnlyUpdates || [], scheduledDateTime);
    setScheduleDate('');
    setScheduleTime('');
  };

  const handleSaveScriptOnly = async () => {
    setScheduleModal((m) => ({ ...m, open: false }));
    await executeSave(scheduleModal.pendingUpdates || [], 'saveScriptOnly', scheduleModal.saveOnlyUpdates || []);
  };

  /* ── Global Save All ─────────────────────────────────────── */
  const getModifiedLocationIds = useCallback(() => {
    const orig = originalEditStateRef.current;
    const origCats = originalCategoryEditRef.current;
    const origTags = originalTagEditRef.current;
    return Object.keys(editState).filter((locId) => {
      const cur = editState[locId];
      const old = orig[locId];
      if (!old) return false;
      if (JSON.stringify(cur) !== JSON.stringify(old)) return true;
      // Also check category changes
      const curCat = categoryEditState[locId] || [];
      const oldCat = origCats[locId] || [];
      if (JSON.stringify([...curCat].sort()) !== JSON.stringify([...oldCat].sort())) return true;
      // Also check tag changes
      const curTag = tagEditState[locId] || [];
      const oldTag = origTags[locId] || [];
      if (JSON.stringify([...curTag].sort()) !== JSON.stringify([...oldTag].sort())) return true;
      return false;
    });
  }, [editState, categoryEditState, tagEditState]);

  const modifiedIds = useMemo(() => getModifiedLocationIds(), [getModifiedLocationIds]);

  const handleSaveAll = async () => {
    if (modifiedIds.length === 0) return;

    const modifiedWithScripts = [];
    const modifiedWithoutScripts = [];

    modifiedIds.forEach(locId => {
      const edits = editState[locId];
      const orig = originalEditStateRef.current[locId];
      let hasScriptChanges = false;

      if (orig && edits.textDirectionsMultiLang) {
        const changedLangs = Object.keys(edits.textDirectionsMultiLang).filter(
          lang => edits.textDirectionsMultiLang[lang] !== orig.textDirectionsMultiLang?.[lang]
        );
        if (changedLangs.length > 0) hasScriptChanges = true;
      }

      if (hasScriptChanges) {
        modifiedWithScripts.push(locId);
      } else {
        modifiedWithoutScripts.push(locId);
      }
    });

    if (modifiedWithScripts.length > 0) {
      // Auto-fill with current date and time + 2 minutes
      const now = new Date();
      now.setMinutes(now.getMinutes() + 2); // Add 2 minutes
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().slice(0, 5); // HH:MM
      setScheduleDate(dateStr);
      setScheduleTime(timeStr);
      
      setScheduleModal({
        open: true,
        pendingUpdates: modifiedWithScripts,
        saveOnlyUpdates: modifiedWithoutScripts,
        locationName: `${modifiedWithScripts.length} Location(s) Ready`,
        lang: null
      });
    } else if (modifiedWithoutScripts.length > 0) {
      await executeSave([], 'saveScriptOnly', modifiedWithoutScripts);
    }
  };


  const getCategoryName = (loc) => loc.categoryName || '';

  /* ── Category multi-select handler ───────────────────────── */
  const handleCategoryChange = (locId, selectedIds) => {
    setCategoryEditState((p) => ({ ...p, [locId]: selectedIds }));
  };

  const getCategoryDisplayName = (cat) => {
    if (!cat) return '';
    if (cat.name && typeof cat.name === 'object') return cat.name.en || Object.values(cat.name)[0] || '';
    return cat.name || '';
  };

  /* ── Tag multi-select handler ────────────────────────────── */
  const handleTagChange = (locId, selectedIds) => {
    setTagEditState((p) => ({ ...p, [locId]: selectedIds }));
  };

  const getTagDisplayName = (tag) => {
    if (!tag) return '';
    if (tag.name && typeof tag.name === 'object') return tag.name.en || Object.values(tag.name)[0] || '';
    return tag.name || '';
  };

  /* ── Caption modal handlers ──────────────────────────────── */
  const openCaptionModal = async (locId, lang, locationName) => {
    setCaptionModal({ open: true, locId, lang, content: '', locationName, captionUrl: '', loading: true, saving: false });
    try {
      const res = await axios.get(`/api/wayfinder/locations/${locId}/caption-content/${lang}`);
      if (res.data.success) {
        setCaptionModal((m) => ({ ...m, content: res.data.data.content || '', captionUrl: res.data.data.captionUrl || '', loading: false }));
      } else {
        setCaptionModal((m) => ({ ...m, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching caption:', err);
      setCaptionModal((m) => ({ ...m, loading: false }));
      setSnackbar({ open: true, message: 'Failed to load caption file', severity: 'error' });
    }
  };

  const handleSaveCaptionContent = async () => {
    setCaptionModal((m) => ({ ...m, saving: true }));
    try {
      await axios.put(`/api/wayfinder/locations/${captionModal.locId}/update-caption-content`, {
        language: captionModal.lang,
        content: captionModal.content,
      });
      setSnackbar({ open: true, message: 'Caption saved successfully!', severity: 'success' });
      setCaptionModal((m) => ({ ...m, open: false, saving: false }));
      await fetchLocations(page, pageSize, search);
    } catch (err) {
      console.error('Error saving caption:', err);
      setSnackbar({ open: true, message: 'Failed to save caption', severity: 'error' });
      setCaptionModal((m) => ({ ...m, saving: false }));
    }
  };

  /* ── Get Text Directions handler ─────────────────────────── */
  const handleGetTextDirections = async (locId, lang) => {
    const loc = locations.find(l => l._id === locId);
    if (!loc || !loc.spaceName) {
      setSnackbar({ open: true, message: 'Location must have a MappedIn space name', severity: 'error' });
      return;
    }

    setFetchingDirections(prev => ({ ...prev, [`${locId}_${lang}`]: true }));

    try {
      // Use mapped-in start location from settings if available
      const startLocation = mappedInConfig?.mappedInStartLocation || null;
      const startLocationName = mappedInConfig?.mappedInStartLocationName || null;

      // Fetch directions from MappedIn
      const result = await fetchDirections(null, loc.spaceName, startLocation, startLocationName);
      
      if (result && result.success) {
        // Get raw directions text (no SSML, no extra formatting)
        const rawDirections = removeSSMLTags(result.text);
        
        // AI Enhancement: enhance ONLY the directions part
        try {
          console.log("[AllLocationsTable] Enhancing directions with AI...");
          console.log("[AllLocationsTable] Raw directions:", rawDirections);
          
          const enhancementResult = await enhanceDirectionsWithAI(
            userId,
            locId,
            rawDirections,
            loc.spaceName,
            loc.spaceName
          );
          
          if (enhancementResult.success && enhancementResult.enhancedDirections) {
            console.log("[AllLocationsTable] AI enhancement successful");
            
            // Now get the concatenated text with code inserts from backend
            const concatenatedResult = await getConcatenatedDirectionsText(
              userId,
              locId,
              loc.spaceName,
              null, // categoryId - backend will determine from location
              null  // subCategoryId - backend will determine from location
            );
            
            if (concatenatedResult.success && concatenatedResult.concatenatedText) {
              const finalText = concatenatedResult.concatenatedText;
              
              console.log("[AllLocationsTable] Final concatenated text:", finalText);
              
              // Update the text directions for this language
              handleTextDirectionChange(locId, lang, finalText);
              
              setSnackbar({ 
                open: true, 
                message: `Text directions fetched and enhanced successfully for ${getLangName(lang)}!`, 
                severity: 'success' 
              });
            } else {
              // Fallback to raw directions if concatenation fails
              handleTextDirectionChange(locId, lang, rawDirections);
              setSnackbar({ 
                open: true, 
                message: `Text directions fetched for ${getLangName(lang)} (concatenation failed)`, 
                severity: 'warning' 
              });
            }
          } else {
            // Fallback to raw directions if AI enhancement fails
            handleTextDirectionChange(locId, lang, rawDirections);
            setSnackbar({ 
              open: true, 
              message: `Text directions fetched for ${getLangName(lang)} (AI enhancement failed)`, 
              severity: 'warning' 
            });
          }
        } catch (enhanceError) {
          console.error("[AllLocationsTable] AI enhancement failed:", enhanceError);
          // Fallback to raw directions
          handleTextDirectionChange(locId, lang, rawDirections);
          setSnackbar({ 
            open: true, 
            message: `Text directions fetched for ${getLangName(lang)} (using original)`, 
            severity: 'info' 
          });
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to fetch directions from MappedIn', severity: 'error' });
      }
    } catch (error) {
      console.error('Error fetching text directions:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to fetch text directions', 
        severity: 'error' 
      });
    } finally {
      setFetchingDirections(prev => ({ ...prev, [`${locId}_${lang}`]: false }));
    }
  };

  /* ── Breaktime tag helper (defined above in CSV section) ── */
  const renderLangEntries = (dataMap, fieldFn) => {
    if (!dataMap || typeof dataMap !== 'object') return <span className="alt-empty">—</span>;
    const entries = Object.entries(dataMap);
    if (!entries.length) return <span className="alt-empty">—</span>;
    const rendered = entries.map(([lang, data]) => {
      const val = fieldFn(data, lang);
      if (!val) return null;
      return (
        <div key={lang} className="alt-lang-row">
          <span className="alt-lang-badge">{lang}</span>
          <span className="alt-lang-value">{val}</span>
        </div>
      );
    }).filter(Boolean);
    return rendered.length ? rendered : <span className="alt-empty">—</span>;
  };

  /* ══════════════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════════════ */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column" gap={2}>
        <CircularProgress sx={{ color: buttonColor }} size={44} />
        <Typography color="text.secondary">Loading locations…</Typography>
      </Box>
    );
  }

  const hasNoLocations = !locations.length && !search;
  const hasNoSearchResults = !locations.length && search;

  return (
    <>
      {/* Scoped styles */}
      <style>{`
        .alt-container { font-family: 'Inter', 'Roboto', -apple-system, sans-serif; }

        /* ── Toolbar ────────────────────────────── */
        .alt-toolbar {
          display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
          justify-content: space-between; margin-bottom: 16px; padding: 0 2px;
        }
        .alt-toolbar-left  { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
        .alt-toolbar-right { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        .alt-search-box {
          display: flex; align-items: center; gap: 6px;
          background: #f5f6fa; border: 1px solid #e0e3eb; border-radius: 10px;
          padding: 3px 14px; min-width: 500px; transition: border-color .2s, box-shadow .2s;
        }
        .alt-search-box:focus-within {
          border-color: ${buttonColor}; box-shadow: 0 0 0 3px ${buttonColor}22;
        }
        .alt-search-box input {
          border: none; outline: none; background: transparent; font-size: 13px;
          flex: 1; color: #333; font-family: inherit;
        }

        .alt-legend { display: flex; gap: 16px; align-items: center; }
        .alt-legend-item { display: flex; gap: 5px; align-items: center; font-size: 11px; color: #777; }
        .alt-legend-swatch {
          width: 14px; height: 14px; border-radius: 3px; border: 1px solid;
        }

        /* ── Selection checkbox ─────────────────── */
        .alt-checkbox {
          width: 18px; height: 18px; cursor: pointer; accent-color: ${buttonColor};
        }
        .alt-checkbox:disabled { cursor: not-allowed; opacity: 0.5; }

        /* ── Table wrapper ──────────────────────── */
        .alt-table-wrap {
          border-radius: 14px; border: 1px solid #e0e3eb;
          box-shadow: 0 4px 24px rgba(0,0,0,0.06);
          overflow-x: auto; overflow-y: hidden; background: #fff;
        }
        .alt-table {
          width: 100%; border-collapse: separate; border-spacing: 0;
          min-width: 2000px; font-size: 13px;
        }

        /* ── Header ─────────────────────────────── */
        .alt-table thead th {
          position: sticky; top: 0; z-index: 10;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          color: #e0e8ff; padding: 13px 14px; text-align: left;
          font-weight: 600; font-size: 11px; text-transform: uppercase;
          letter-spacing: 0.7px; border-bottom: 2px solid #0f3460;
          white-space: nowrap; user-select: none;
        }
        .alt-table thead th.alt-th-editable {
          background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
          color: #c8e6c9;
        }
        .alt-table thead th.alt-th-action {
          position: sticky; right: 0; z-index: 11; text-align: center;
          box-shadow: -6px 0 12px rgba(0,0,0,0.08);
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        }

        /* ── Cells ──────────────────────────────── */
        .alt-td-ro {
          background: #fef9f0; padding: 10px 12px;
          border-bottom: 1px solid #f0ece4; border-right: 1px solid #f5f1ea;
          vertical-align: top; color: #444; min-width: 110px;
        }
        .alt-td-edit {
          background: #ffffff; padding: 8px 10px;
          border-bottom: 1px solid #eef0f5; border-right: 1px solid #f5f6fa;
          vertical-align: top; min-width: 250px;
        }

        .alt-td-edit-min {
         background: #ffffff; padding: 8px 10px;
          border-bottom: 1px solid #eef0f5; border-right: 1px solid #f5f6fa;
          vertical-align: top; min-width: 100px;
        }

        .alt-td-action {
          background: #fafbfd; padding: 10px 12px;
          border-bottom: 1px solid #eef0f5; vertical-align: middle; text-align: center;
          position: sticky; right: 0; z-index: 5; min-width: 100px;
          box-shadow: -6px 0 12px rgba(0,0,0,0.04);
        }

        /* Zebra */
        .alt-table tbody tr:nth-child(even) .alt-td-ro    { background: #fdf6e7; }
        .alt-table tbody tr:nth-child(even) .alt-td-edit  { background: #f9fbff; }
        .alt-table tbody tr:nth-child(even) .alt-td-action { background: #f5f7fa; }

        /* Hover */
        .alt-table tbody tr:hover .alt-td-ro    { background: #fff3d6 !important; }
        .alt-table tbody tr:hover .alt-td-edit  { background: #f0f7ff !important; }
        .alt-table tbody tr:hover .alt-td-action { background: #eef2f8 !important; }

        /* Pending Video Generation - Highlight rows with unsaved text direction changes */
        .alt-table tbody tr.alt-row-pending-video .alt-td-ro {
          background: #fff8e1 !important;
        }
        .alt-table tbody tr.alt-row-pending-video .alt-td-ro:first-child {
          box-shadow: inset 4px 0 0 0 #ff9800;
        }
        .alt-table tbody tr.alt-row-pending-video .alt-td-edit {
          background: #fffbf0 !important;
        }
        .alt-table tbody tr.alt-row-pending-video .alt-td-edit-min {
          background: #fffbf0 !important;
        }
        .alt-table tbody tr.alt-row-pending-video .alt-td-action {
          background: #fff8e1 !important;
        }
        .alt-table tbody tr.alt-row-pending-video:hover .alt-td-ro {
          background: #ffe082 !important;
        }
        .alt-table tbody tr.alt-row-pending-video:hover .alt-td-edit {
          background: #fff3c4 !important;
        }
        .alt-table tbody tr.alt-row-pending-video:hover .alt-td-edit-min {
          background: #fff3c4 !important;
        }
        .alt-table tbody tr.alt-row-pending-video:hover .alt-td-action {
          background: #ffe082 !important;
        }

        /* ── Inputs ─────────────────────────────── */
        .alt-input, .alt-textarea {
          width: 100%; font-size: 13px; padding: 7px 10px;
          border: 1.5px solid #dde1ea; border-radius: 8px; outline: none;
          font-family: inherit; transition: border-color .2s, box-shadow .2s;
          background: #fff;
        }
        .alt-input:focus, .alt-textarea:focus {
          border-color: ${buttonColor}; box-shadow: 0 0 0 3px ${buttonColor}18;
        }
        .alt-textarea { resize: vertical; min-height: 56px; line-height: 1.45; }

        /* ── Lang badges ────────────────────────── */
        .alt-lang-row { margin-bottom: 5px; line-height: 1.35; display: flex; align-items: flex-start; gap: 5px; }
        .alt-lang-badge {
          display: inline-block; font-size: 9px; font-weight: 700;
          text-transform: uppercase; color: #1565c0; background: #e3f2fd;
          padding: 2px 6px; border-radius: 4px; letter-spacing: 0.3px;
          flex-shrink: 0; margin-top: 2px;
        }
        .alt-lang-value { font-size: 12px; color: #444; word-break: break-word; }
        .alt-empty { color: #c0c0c0; font-style: italic; font-size: 12px; }

        /* ── Map link ───────────────────────────── */
        .alt-map-link {
          color: #1565c0; text-decoration: none; font-size: 12px; font-weight: 500;
          display: inline-flex; align-items: center; gap: 4px;
          padding: 4px 8px; border-radius: 6px; transition: background .15s;
        }
        .alt-map-link:hover { background: #e3f2fd; }

        /* ── Pagination ─────────────────────────── */
        .alt-pagination {
          display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
          justify-content: space-between; margin-top: 16px; padding: 0 4px;
        }
        .alt-pagination-info { font-size: 13px; color: #666; }
        .alt-pagination-controls { display: flex; gap: 4px; align-items: center; }
        .alt-page-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid #dde1ea;
          background: #fff; cursor: pointer; display: flex; align-items: center;
          justify-content: center; transition: all .15s; color: #555;
        }
        .alt-page-btn:hover:not(:disabled) { background: ${buttonColor}; color: #fff; border-color: ${buttonColor}; }
        .alt-page-btn:disabled { opacity: .35; cursor: default; }
        .alt-page-btn.active { background: ${buttonColor}; color: #fff; border-color: ${buttonColor}; font-weight: 700; }

        .alt-page-size-select {
          padding: 6px 10px; border: 1px solid #dde1ea; border-radius: 8px;
          font-size: 13px; background: #fff; outline: none; cursor: pointer;
          font-family: inherit;
        }

        /* ── Row number ─────────────────────────── */
        .alt-row-num {
          font-weight: 700; color: #aaa; font-size: 11px; text-align: center;
          font-variant-numeric: tabular-nums;
        }

        /* ── Focus ring for textareas in lang blocks ── */
        .alt-lang-edit-block { margin-bottom: 10px; }
        .alt-lang-edit-block:last-child { margin-bottom: 0; }
      `}</style>

      <Box className="alt-container">
        {/* ═══ Header ═══ */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.3px' }}>
              All Locations
            </Typography>
            <Chip
              label={`${totalCount}${search ? ` (filtered)` : ''}`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: buttonColor, color: '#fff', fontSize: '12px', height: 26 }}
            />
          </Box>
        </Box>

        {/* ═══ Toolbar ═══ */}
        <div className="alt-toolbar">
          <div className="alt-toolbar-left">
            <div className="alt-search-box">
              <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, space ID, category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: '2px' }}>
                  <span style={{ fontSize: 14, color: '#999' }}>✕</span>
                </IconButton>
              )}
            </div>
          </div>

          <div className="alt-toolbar-right">
            <Tooltip title="Refresh to see latest data (including pending video states from bulk generation)">
              <IconButton onClick={() => fetchLocations(page, pageSize, search)} size="small" sx={{ border: '1px solid #dde1ea', borderRadius: '8px', p: '6px' }}>
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            
            {selectedLocations.size > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={handleDeleteSelected}
                sx={{
                  borderColor: '#ef5350', color: '#ef5350', textTransform: 'none',
                  fontWeight: 600, fontSize: '12px', borderRadius: '8px', px: 2, py: 0.7,
                  '&:hover': { borderColor: '#d32f2f', color: '#d32f2f', background: '#ffebee' },
                }}
              >
                Delete ({selectedLocations.size})
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => exportToCSV(locations, hideBreakTags)}
              sx={{
                borderColor: '#dde1ea', color: '#444', textTransform: 'none',
                fontWeight: 600, fontSize: '12px', borderRadius: '8px', px: 2, py: 0.7,
                '&:hover': { borderColor: buttonColor, color: buttonColor, background: `${buttonColor}08` },
              }}
            >
              Export CSV{hideBreakTags && ' (No Tags)'}
            </Button>
            {modifiedIds.length > 0 && (
              <Tooltip title={hideBreakTags ? 'Cannot save while breaktime tags are hidden' : ''}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={savingAll ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSaveAll}
                    disabled={savingAll || hideBreakTags}
                    sx={{
                      bgcolor: '#2e7d32', color: '#fff', textTransform: 'none',
                      fontWeight: 600, fontSize: '12px', borderRadius: '8px', px: 2, py: 0.7,
                      boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                      '&:hover': { bgcolor: '#1b5e20' },
                    }}
                  >
                    {savingAll ? 'Saving…' : `Save All (${modifiedIds.length})`}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Breaktime tag toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideBreakTags}
                  onChange={(e) => setHideBreakTags(e.target.checked)}
                  size="small"
                  sx={{ color: '#ef6c00', '&.Mui-checked': { color: '#ef6c00' } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: hideBreakTags ? '#ef6c00' : '#666' }}>
                  Hide &lt;breaktime&gt; tags
                </Typography>
              }
              sx={{ ml: 0.5 }}
            />
          </div>
        </div>

        {/* ═══ Table ═══ */}
        <div className="alt-table-wrap">
          {hasNoLocations ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px" p={4}>
              <Typography variant="h6" color="text.secondary">
                No locations found. Create locations in the Location Manager tab.
              </Typography>
            </Box>
          ) : hasNoSearchResults ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="300px" p={4} gap={2}>
              <SearchIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />
              <Typography variant="h6" color="text.secondary">
                No locations match your search
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or <Button onClick={() => setSearch('')} sx={{ textTransform: 'none', p: 0, minWidth: 'auto', textDecoration: 'underline' }}>clear the search</Button>
              </Typography>
            </Box>
          ) : (
          <table className="alt-table">
            <thead>
              <tr>
                <th style={{ minWidth: 50, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    className="alt-checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) input.indeterminate = isSomeSelected;
                    }}
                    onChange={handleSelectAll}
                    disabled={hideBreakTags}
                    title={hideBreakTags ? 'Selection disabled while tags are hidden' : 'Select all'}
                  />
                </th>
                <th style={{ minWidth: 10, textAlign: 'center' }}>S. No.</th>
                <th>Location Name</th>
                <th>Map ID</th>
                <th className="alt-th-editable" style={{ minWidth: 180 }}>Category</th>
                <th className="alt-th-editable" style={{ minWidth: 180 }}>Tags</th>
                <th>Avatar File Name</th>
                <th style={{ minWidth: 55, textAlign: 'center' }}>Steps</th>
                <th>Duration</th>
                <th style={{ minWidth: 120 }}>Caption File</th>
                <th className="alt-th-editable" style={{ minWidth: 20 }}>Delay</th>
                <th className="alt-th-editable" style={{ minWidth: 450 }}>Text Directions (ENG){hideBreakTags && <Chip label="TAGS HIDDEN" size="small" sx={{ ml: 1, bgcolor: '#ef6c00', color: '#fff', fontSize: 9, height: 18 }} />}</th>
                <th className="alt-th-editable" style={{ minWidth: 450 }}>Text Directions (Other){hideBreakTags && <Chip label="TAGS HIDDEN" size="small" sx={{ ml: 1, bgcolor: '#ef6c00', color: '#fff', fontSize: 9, height: 18 }} />}</th>
                <th className="alt-th-action" style={{ minWidth: 100 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((loc, idx) => {
                const edit = editState[loc._id] || {};
                const isSaving = savingRows[loc._id] || false;
                const mapLink = buildMapLink(
                  loc.externalId, 
                  mappedInConfig?.mapLink, 
                  mappedInConfig?.mappedInStartLocation
                );
                const categoryName = getCategoryName(loc);
                const locationName = getLanguageText(loc.name, 'en');
                const allLangs = edit.textDirectionsMultiLang ? Object.keys(edit.textDirectionsMultiLang) : ['en'];
                const otherLangs = allLangs.filter((l) => l !== 'en');
                const globalIdx = page * pageSize + idx;

                // Check if text directions have been modified but video not regenerated
                // Check both: 1) database flag, 2) unsaved changes in current session
                const orig = originalEditStateRef.current[loc._id];
                let hasUnGeneratedChanges = false;
                
                // First check if there's a database flag indicating text was changed
                if (loc.textDirectionsChanged === true) {
                  hasUnGeneratedChanges = true;
                  console.log(`[VideoPending] ${loc.spaceName || locationName}: Database flag set (textDirectionsChanged = ${loc.textDirectionsChanged})`);
                }
                
                // Also check for unsaved changes in current session
                if (!hasUnGeneratedChanges && orig && edit && edit.textDirectionsMultiLang) {
                  const allEditLangs = Object.keys(edit.textDirectionsMultiLang);
                  const changedLangs = allEditLangs.filter(lang => {
                    const currentText = (edit.textDirectionsMultiLang[lang] || '').trim();
                    const originalText = (orig.textDirectionsMultiLang?.[lang] || '').trim();
                    const hasChanged = currentText !== originalText;
                    
                    if (hasChanged && currentText.length > 0) {
                      console.log(`[VideoPending] ${loc.spaceName || locationName} (${lang}): Unsaved changes detected`);
                      console.log(`  Original length: ${originalText.length}, Current length: ${currentText.length}`);
                    }
                    
                    return hasChanged && currentText.length > 0;
                  });
                  
                  hasUnGeneratedChanges = changedLangs.length > 0;
                } else if (!hasUnGeneratedChanges) {
                  console.log(`[VideoPending] ${loc.spaceName || locationName}: No pending changes (DB flag: ${loc.textDirectionsChanged}, has orig: ${!!orig}, has edit: ${!!edit})`);
                }

                return (
                  <tr 
                    key={loc._id}
                    className={hasUnGeneratedChanges ? 'alt-row-pending-video' : ''}
                  >
                    {/* Selection Checkbox */}
                    <td className="alt-td-ro" style={{ textAlign: 'center', padding: '10px 8px' }}>
                      <input
                        type="checkbox"
                        className="alt-checkbox"
                        checked={selectedLocations.has(loc._id)}
                        onChange={() => handleSelectLocation(loc._id)}
                        disabled={hideBreakTags}
                        title={hideBreakTags ? 'Selection disabled while tags are hidden' : ''}
                      />
                    </td>

                    {/* # */}
                    <td className="alt-td-ro alt-row-num">
                      <a
                        href={`/location/${userId}/${loc._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ cursor: 'pointer', color: '#1565c0', textDecoration: 'underline' }}
                        title="Click to view location directions in new tab"
                      >
                        {globalIdx + 1}
                      </a>
                    </td>

                    {/* Location Name MappedIn */}
                    <td className="alt-td-ro">
                      <a
                        href={`/wayfinder-locations/${userId}/${loc._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        title="Open Location Settings"
                      >
                        {loc.spaceName && loc.spaceName.trim() !== '' ? loc.spaceName : locationName || <span className="alt-empty">—</span>}
                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </a>
                    </td>

                    {/* Space ID */}
                    <td className="alt-td-ro">
                      {mapLink ? (
                        <Tooltip title="Open Map Link">
                          <a href={mapLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <code style={{ fontSize: 12, background: '#f0ece4', padding: '2px 6px', borderRadius: 4, color: '#1565c0' }}>
                              {loc.externalId || <span className="alt-empty">—</span>}
                            </code>
                          </a>
                        </Tooltip>
                      ) : (
                        <code style={{ fontSize: 12, background: '#f0ece4', padding: '2px 6px', borderRadius: 4 }}>
                          {loc.externalId || <span className="alt-empty">—</span>}
                        </code>
                      )}
                    </td>

                    {/* Category (Multi-select) */}
                    <td className="alt-td-edit" style={{ minWidth: 180, verticalAlign: 'top', paddingTop: '8px' }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          multiple
                          displayEmpty
                          value={categoryEditState[loc._id] || []}
                          onChange={(e) => handleCategoryChange(loc._id, e.target.value)}
                          input={
                            <OutlinedInput
                              sx={{
                                borderRadius: '8px',
                                backgroundColor: '#f8fafc',
                                transition: 'all 0.2s',
                                '&:hover': { backgroundColor: '#f1f5f9' },
                                '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)' }
                              }}
                            />
                          }
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return <Typography sx={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', pl: 0.5 }}>Select categories...</Typography>;
                            }
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                {selected.map((id) => {
                                  const cat = allCategories.find(c => (c._id || c.id) === id);
                                  return (
                                    <Chip
                                      key={id}
                                      label={cat ? getCategoryDisplayName(cat) : id}
                                      size="small"
                                      sx={{
                                        fontSize: 10,
                                        height: 22,
                                        fontWeight: 600,
                                        backgroundColor: '#e0e7ff',
                                        color: '#3730a3',
                                        border: '1px solid #c7d2fe',
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          }}
                          sx={{
                            fontSize: 13,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                            '& .MuiSelect-select': { py: 1 }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 320,
                                borderRadius: '12px',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                mt: 0.8,
                                border: '1px solid #f1f5f9',
                                '& .MuiMenuItem-root': {
                                  borderRadius: '6px',
                                  mx: 1,
                                  my: 0.3,
                                  '&:hover': { backgroundColor: '#f8fafc' },
                                  '&.Mui-selected': { backgroundColor: '#111a25ff', '&:hover': { backgroundColor: '#01070aff' } }
                                }
                              }
                            }
                          }}
                        >
                          {allCategories
                            .filter((cat, index, self) => {
                              // Remove duplicates based on _id - convert to string for comparison
                              const catId = (cat._id || cat.id)?.toString();
                              return index === self.findIndex((c) => (c._id || c.id)?.toString() === catId);
                            })
                            .map((cat) => {
                            const catId = cat._id || cat.id;
                            const catName = getCategoryDisplayName(cat);
                            const isSelected = (categoryEditState[loc._id] || []).indexOf(catId) > -1;
                            return (
                              <MenuItem
                                key={catId}
                                value={catId}
                                dense
                                sx={{
                                  ...(isSelected && {
                                    backgroundColor: '#1772e9ff !important',
                                    color: '#fff !important',
                                    '&:hover': { backgroundColor: '#4caff1ff !important' }
                                  })
                                }}
                              >
                                <ListItemText
                                  primary={catName}
                                  primaryTypographyProps={{
                                    fontSize: 13,
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? '#ffffff' : '#475569'
                                  }}
                                />
                              </MenuItem>
                            );
                          })}

                        </Select>
                      </FormControl>
                    </td>

                    {/* Tags (Multi-select) */}
                    <td className="alt-td-edit" style={{ minWidth: 180, verticalAlign: 'top', paddingTop: '8px' }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          multiple
                          displayEmpty
                          value={tagEditState[loc._id] || []}
                          onChange={(e) => handleTagChange(loc._id, e.target.value)}
                          input={
                            <OutlinedInput
                              sx={{
                                borderRadius: '8px',
                                backgroundColor: '#f8fafc',
                                transition: 'all 0.2s',
                                '&:hover': { backgroundColor: '#f1f5f9' },
                                '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)' }
                              }}
                            />
                          }
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return <Typography sx={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', pl: 0.5 }}>Select tags...</Typography>;
                            }
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                {selected.map((id) => {
                                  const tag = allTags.find(t => t._id === id);
                                  return (
                                    <Box
                                      key={id}
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1.2,
                                        py: 0.4,
                                        borderRadius: '12px',
                                        backgroundColor: tag?.color || '#94a3b8',
                                        color: '#fff',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        height: 22,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: '#fff',
                                          opacity: 0.8,
                                        }}
                                      />
                                      {tag ? getTagDisplayName(tag) : id}
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          }}
                          sx={{
                            fontSize: 13,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                            '& .MuiSelect-select': { py: 1 }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 320,
                                borderRadius: '12px',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                mt: 0.8,
                                border: '1px solid #f1f5f9',
                                '& .MuiMenuItem-root': {
                                  borderRadius: '6px',
                                  mx: 1,
                                  my: 0.3,
                                  '&:hover': { backgroundColor: '#f8fafc' },
                                  '&.Mui-selected': { backgroundColor: '#111a25ff', '&:hover': { backgroundColor: '#01070aff' } }
                                }
                              }
                            }
                          }}
                        >
                          {allTags.map((tag) => {
                            const isSelected = (tagEditState[loc._id] || []).indexOf(tag._id) > -1;
                            return (
                              <MenuItem
                                key={tag._id}
                                value={tag._id}
                                dense
                                sx={{
                                  ...(isSelected && {
                                    backgroundColor: `${tag.color}dd !important`,
                                    color: '#fff !important',
                                    '&:hover': { backgroundColor: `${tag.color} !important` }
                                  })
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: tag.color,
                                      border: isSelected ? '2px solid #fff' : 'none'
                                    }}
                                  />
                                  <ListItemText
                                    primary={getTagDisplayName(tag)}
                                    primaryTypographyProps={{
                                      fontSize: 13,
                                      fontWeight: isSelected ? 600 : 400,
                                      color: isSelected ? '#ffffff' : '#475569'
                                    }}
                                  />
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </td>

                    {/* HeyGen File Name */}
                    <td className="alt-td-ro">
                      {renderLangEntries(loc.avatarVideosMultiLang, (d) =>
                        d?.fileName && d?.fileUrl ? (
                          <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', wordBreak: 'break-all' }}>
                            {d.fileName}
                          </a>
                        ) : (
                          d?.fileName
                        )
                      )}
                    </td>

                    {/* # Steps */}
                    <td className="alt-td-ro" style={{ textAlign: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#444' }}>{loc.totalSteps || 0}</span>
                    </td>

                    {/* Video Duration */}
                    <td className="alt-td-ro">
                      {renderLangEntries(loc.avatarVideosMultiLang, (d) => {
                        const dur = d?.videoDuration;
                        if (!dur) return null;
                        return `${Math.round(dur / 1000)}s`;
                      })}
                    </td>

                    {/* Caption File */}
                    <td className="alt-td-ro" style={{ minWidth: 120 }}>
                      {(() => {
                        if (!loc.avatarVideosMultiLang || typeof loc.avatarVideosMultiLang !== 'object') return <span className="alt-empty">—</span>;
                        const captionEntries = Object.entries(loc.avatarVideosMultiLang)
                          .filter(([, data]) => data?.captionFile)
                          .map(([lang, data]) => ({ lang, url: data.captionFile, fileName: data.captionFile.split('/').pop() || `${lang}.vtt` }));
                        if (captionEntries.length === 0) return <span className="alt-empty">—</span>;
                        return captionEntries.map(({ lang, fileName }) => (
                          <div key={lang} className="alt-lang-row" style={{ cursor: 'pointer' }}>
                            <span className="alt-lang-badge">{lang}</span>
                            <span
                              className="alt-lang-value"
                              style={{ color: '#1565c0', cursor: 'pointer', fontSize: 11 }}
                              onClick={() => openCaptionModal(loc._id, lang, locationName)}
                              title="Click to view/edit caption"
                            >
                              <DescriptionIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                              {fileName.length > 20 ? fileName.substring(0, 20) + '...' : fileName}
                            </span>
                          </div>
                        ));
                      })()}
                    </td>

                    {/* ── Map Delay Time (EDITABLE) ── */}
                    <td className="alt-td-edit-min" style={{ width: '100px' }}>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        className="alt-input"
                        value={edit.mapDelayTime ?? ''}
                        onChange={(e) => handleMapDelayChange(loc._id, e.target.value)}
                        placeholder="sec"
                      />
                      <div style={{ fontSize: 10, color: '#999', marginTop: 3, paddingLeft: 2 }}>seconds</div>
                    </td>

                    {/* ── Text Directions ENG (EDITABLE) ── */}
                    <td className="alt-td-edit">
                      {hideBreakTags ? (
                        <div
                          style={{
                            fontSize: 13, lineHeight: 1.7, color: '#334155', padding: '10px 12px',
                            background: '#f0fdf4', borderRadius: 8, border: '1px dashed #86efac',
                            minHeight: 80, whiteSpace: 'normal', wordBreak: 'break-word',
                          }}
                        >
                          {stripBreakTags(edit.textDirectionsMultiLang?.en ?? '') || <span className="alt-empty">—</span>}
                        </div>
                      ) : (
                        <>
                          <textarea
                            className="alt-textarea"
                            value={edit.textDirectionsMultiLang?.en ?? ''}
                            onChange={(e) => handleTextDirectionChange(loc._id, 'en', e.target.value)}
                            placeholder="English text directions…"
                            rows={5}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                              onClick={() => openViewModal(loc._id, 'en', locationName)}
                              sx={{
                                textTransform: 'none', fontSize: 11, color: '#1565c0',
                                fontWeight: 600, p: '2px 8px', borderRadius: '6px', minWidth: 0,
                                '&:hover': { background: '#e3f2fd' },
                              }}
                            >
                              View & Edit
                            </Button>
                            <Button
                              size="small"
                              startIcon={
                                fetchingDirections[`${loc._id}_en`] 
                                  ? <CircularProgress size={11} sx={{ color: '#17a2b8' }} />
                                  : <GetAppIcon sx={{ fontSize: 13 }} />
                              }
                              onClick={() => handleGetTextDirections(loc._id, 'en')}
                              disabled={fetchingDirections[`${loc._id}_en`] || !loc.spaceName}
                              sx={{
                                textTransform: 'none', fontSize: 11, color: '#17a2b8',
                                fontWeight: 600, p: '2px 8px', borderRadius: '6px', minWidth: 0,
                                '&:hover': { background: '#e0f7fa' },
                                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                              }}
                              title={!loc.spaceName ? 'Location must have a MappedIn space name' : 'Fetch directions from MappedIn'}
                            >
                              {fetchingDirections[`${loc._id}_en`] ? 'Fetching...' : 'Get Directions'}
                            </Button>
                          </Box>
                        </>
                      )}
                    </td>

                    {/* ── Text Directions Other (EDITABLE) ── */}
                    <td className="alt-td-edit">
                      {otherLangs.length > 0 ? (
                        otherLangs.map((lang) => (
                          <div key={lang} className="alt-lang-edit-block">

                            {hideBreakTags ? (
                              <div
                                style={{
                                  fontSize: 12, lineHeight: 1.7, color: '#334155', padding: '8px 10px',
                                  background: '#f0fdf4', borderRadius: 8, border: '1px dashed #86efac',
                                  minHeight: 60, whiteSpace: 'normal', wordBreak: 'break-word',
                                }}
                              >
                                {stripBreakTags(edit.textDirectionsMultiLang?.[lang] ?? '') || <span className="alt-empty">—</span>}
                              </div>
                            ) : (
                              <textarea
                                className="alt-textarea"
                                style={{ minHeight: 100 }}
                                value={edit.textDirectionsMultiLang?.[lang] ?? ''}
                                onChange={(e) => handleTextDirectionChange(loc._id, lang, e.target.value)}
                                placeholder={`${getLangName(lang)}…`}
                                rows={4}
                              />
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                              <span className="alt-lang-badge" style={{ marginBottom: 0, display: 'inline-block' }}>
                                {getLangName(lang)}
                              </span>
                              {!hideBreakTags && (
                                <>
                                  <Button
                                    size="small"
                                    startIcon={<VisibilityIcon sx={{ fontSize: 11 }} />}
                                    onClick={() => openViewModal(loc._id, lang, locationName)}
                                    sx={{
                                      textTransform: 'none', fontSize: 10, color: '#1565c0',
                                      fontWeight: 600, p: '1px 6px', borderRadius: '4px', minWidth: 0, lineHeight: 1.2,
                                      '&:hover': { background: '#e3f2fd' },
                                    }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={
                                      fetchingDirections[`${loc._id}_${lang}`] 
                                        ? <CircularProgress size={9} sx={{ color: '#17a2b8' }} />
                                        : <GetAppIcon sx={{ fontSize: 11 }} />
                                    }
                                    onClick={() => handleGetTextDirections(loc._id, lang)}
                                    disabled={fetchingDirections[`${loc._id}_${lang}`] || !loc.spaceName}
                                    sx={{
                                      textTransform: 'none', fontSize: 10, color: '#17a2b8',
                                      fontWeight: 600, p: '1px 6px', borderRadius: '4px', minWidth: 0, lineHeight: 1.2,
                                      '&:hover': { background: '#e0f7fa' },
                                      '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                                    }}
                                    title={!loc.spaceName ? 'Location must have a MappedIn space name' : 'Fetch directions from MappedIn'}
                                  >
                                    {fetchingDirections[`${loc._id}_${lang}`] ? 'Fetching...' : 'Get'}
                                  </Button>
                                </>
                              )}
                            </Box>
                          </div>
                        ))
                      ) : (
                        <span className="alt-empty">No other languages</span>
                      )}
                    </td>


                    {/* Submit */}
                    <td className="alt-td-action">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                        {hasUnGeneratedChanges && !isSaving && (
                          <Chip
                            label="Video Pending"
                            size="small"
                            icon={<ScheduleIcon sx={{ fontSize: 12, color: '#fff !important' }} />}
                            sx={{
                              bgcolor: '#ff9800',
                              color: '#fff',
                              fontSize: 9,
                              height: 20,
                              fontWeight: 700,
                              letterSpacing: '0.3px',
                              boxShadow: '0 2px 6px rgba(255, 152, 0, 0.3)',
                              '& .MuiChip-icon': { ml: 0.5 }
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                          {/* Show two buttons when video is pending */}
                          {hasUnGeneratedChanges ? (
                            <>
                              <Tooltip title="Open video generation modal">
                                <span>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleGenerateVideoClick(loc._id)}
                                    disabled={isSaving || hideBreakTags}
                                    startIcon={<FlashOnIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                      bgcolor: '#ff9800',
                                      '&:hover': { bgcolor: '#f57c00', filter: 'brightness(0.85)' },
                                      textTransform: 'none', fontWeight: 600, fontSize: 12,
                                      borderRadius: '8px', px: 2, py: 0.7,
                                      boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                                      minWidth: 120,
                                    }}
                                  >
                                    Generate Video
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Save changes without generating video">
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleSubmitOnlyClick(loc._id)}
                                    disabled={isSaving || hideBreakTags}
                                    startIcon={
                                      isSaving
                                        ? <CircularProgress size={14} sx={{ color: buttonColor }} />
                                        : <SaveIcon sx={{ fontSize: 15 }} />
                                    }
                                    sx={{
                                      borderColor: buttonColor,
                                      color: buttonColor,
                                      '&:hover': { 
                                        borderColor: buttonColor, 
                                        bgcolor: `${buttonColor}10`,
                                        filter: 'brightness(0.85)' 
                                      },
                                      textTransform: 'none', fontWeight: 600, fontSize: 12,
                                      borderRadius: '8px', px: 2, py: 0.7,
                                      minWidth: 80,
                                    }}
                                  >
                                    {isSaving ? 'Saving…' : 'Submit'}
                                  </Button>
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            /* Show single Submit button when no video pending */
                            <Tooltip title={hideBreakTags ? 'Cannot save while breaktime tags are hidden' : ''}>
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleSubmitRow(loc._id)}
                                  disabled={isSaving || hideBreakTags}
                                  startIcon={
                                    isSaving
                                      ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                      : <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                                  }
                                  sx={{
                                    bgcolor: buttonColor,
                                    '&:hover': { bgcolor: buttonColor, filter: 'brightness(0.85)' },
                                    textTransform: 'none', fontWeight: 600, fontSize: 12,
                                    borderRadius: '8px', px: 2, py: 0.7,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    minWidth: 80,
                                  }}
                                >
                                  {isSaving ? 'Saving…' : 'Submit'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Delete location">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSingle(loc)}
                              sx={{
                                color: '#ef5350',
                                border: '1px solid #ef5350',
                                borderRadius: '8px',
                                p: '6px',
                                '&:hover': {
                                  bgcolor: '#ffebee',
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>

        {/* ═══ Pagination ═══ */}
        <div className="alt-pagination">
          <div className="alt-pagination-info">
            Showing <b>{totalCount === 0 ? 0 : page * pageSize + 1}</b>–<b>{Math.min((page + 1) * pageSize, totalCount)}</b> of <b>{totalCount}</b> locations
            {search && ` (filtered)`}
          </div>

          <div className="alt-pagination-controls">
            <label style={{ fontSize: 12, color: '#777', marginRight: 4 }}>Rows:</label>
            <select
              className="alt-page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <span style={{ width: 16 }} />

            <button className="alt-page-btn" disabled={page === 0} onClick={() => setPage(0)} title="First page">
              <FirstPageIcon sx={{ fontSize: 18 }} />
            </button>
            <button className="alt-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)} title="Previous">
              <NavigateBeforeIcon sx={{ fontSize: 18 }} />
            </button>

            {/* Page numbers */}
            {(() => {
              const btns = [];
              const maxVisible = 5;
              let start = Math.max(0, page - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible);
              if (end - start < maxVisible) start = Math.max(0, end - maxVisible);

              for (let i = start; i < end; i++) {
                btns.push(
                  <button
                    key={i}
                    className={`alt-page-btn${i === page ? ' active' : ''}`}
                    onClick={() => setPage(i)}
                  >
                    {i + 1}
                  </button>
                );
              }
              return btns;
            })()}

            <button className="alt-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} title="Next">
              <NavigateNextIcon sx={{ fontSize: 18 }} />
            </button>
            <button className="alt-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)} title="Last page">
              <LastPageIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </Box>

      {/* ═══ View/Edit Text Direction Modal ═══ */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal((m) => ({ ...m, open: false }))}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.5)' } } }}
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3,
        }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px', mb: 0.2 }}>
              <EditIcon sx={{ fontSize: 18, mr: 0.8, verticalAlign: 'middle', color: '#fff' }} />
              Edit Text Directions
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}>
              {viewModal.locationName} <span style={{ opacity: 0.5 }}>•</span> <span className="alt-lang-badge" style={{ fontSize: 10, margin: 0 }}>{getLangName(viewModal.lang)}</span>
            </Typography>
          </Box>
          <IconButton onClick={() => setViewModal((m) => ({ ...m, open: false }))} sx={{ color: '#64748b', '&:hover': { color: '#f8fafc', background: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ position: 'relative' }}>
            {hideBreakTags && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}>
                Showing plain text without &lt;breaktime&gt; tags. Saving is disabled in this mode.
              </Alert>
            )}
            <textarea
              style={{
                width: '100%', minHeight: 280, fontSize: 15, padding: '18px 20px', borderRadius: 14,
                border: hideBreakTags ? '2px dashed #86efac' : '2px solid #cbd5e1',
                fontFamily: 'Inter, Roboto, sans-serif',
                outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box',
                backgroundColor: hideBreakTags ? '#f0fdf4' : '#ffffff',
                color: '#334155', boxShadow: '0 2px 4px rgba(0,0,0,0.02) inset',
                transition: 'all 0.25s ease'
              }}
              value={hideBreakTags ? stripBreakTags(viewModal.text) : viewModal.text}
              onChange={(e) => setViewModal((m) => ({ ...m, text: e.target.value }))}
              placeholder={`Enter ${getLangName(viewModal.lang)} text directions…`}
              readOnly={hideBreakTags}
              onFocus={(e) => { if (!hideBreakTags) { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = `0 0 0 4px rgba(59, 130, 246, 0.15)`; } }}
              onBlur={(e) => { e.target.style.borderColor = hideBreakTags ? '#86efac' : '#cbd5e1'; e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02) inset'; }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setViewModal((m) => ({ ...m, open: false }))} sx={{ textTransform: 'none', color: '#64748b', px: 2, fontWeight: 600 }}>
            {hideBreakTags ? 'Close' : 'Cancel'}
          </Button>
          {!hideBreakTags && (
            <Button
              variant="contained"
              onClick={handleViewModalSubmit}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              sx={{
                background: 'linear-gradient(to right, #3b82f6, #2563eb)', color: '#fff',
                textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, py: 0.8,
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                '&:hover': { background: 'linear-gradient(to right, #2563eb, #1d4ed8)' },
              }}
            >
              Save & Continue
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══ Schedule / Generate / Cancel Modal ═══ */}
      <Dialog
        open={scheduleModal.open}
        onClose={() => setScheduleModal((m) => ({ ...m, open: false }))}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.4)' } } }}
        PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc', textAlign: 'center', py: 3, px: 4, position: 'relative'
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.3px', mb: 0.5, color: "#fff" }}>
            Video Generation Options
          </Typography>
          <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: 13, background: 'rgba(255,255,255,0.1)', padding: '2px 10px', borderRadius: 20 }}>
            {scheduleModal.locationName} {scheduleModal.lang && <><span style={{ mx: 0.5 }}>•</span> {getLangName(scheduleModal.lang)}</>}
          </Typography>
          <IconButton
            onClick={() => setScheduleModal((m) => ({ ...m, open: false }))}
            sx={{ position: 'absolute', right: 16, top: 16, color: '#94a3b8', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3.5, pb: 1, px: 4, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Generate Now */}
            <Button
              variant="contained"
              fullWidth
              startIcon={<FlashOnIcon sx={{ fontSize: 22 }} />}
              onClick={handleGenerateNow}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', textTransform: 'none', fontWeight: 800, borderRadius: '14px', py: 1.8,
                fontSize: 16, color: '#fff', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(16, 185, 129, 0.4)' },
              }}
            >
              Generate Now
            </Button>

            {/* Schedule Option */}
            <Paper
              elevation={0}
              sx={{
                p: 3, border: '2px solid #e2e8f0', borderRadius: '16px', background: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': { borderColor: '#93c5fd', background: '#f0f9ff', boxShadow: '0 8px 24px rgba(59, 130, 246, 0.08)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ p: 1, borderRadius: '10px', background: '#e0f2fe', color: '#0284c7' }}>
                  <ScheduleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Schedule for Later</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Date</Typography>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Time</Typography>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={handleScheduleLater}
                disabled={isScheduling || !scheduleDate || !scheduleTime}
                startIcon={isScheduling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ScheduleIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', textTransform: 'none', fontWeight: 700, borderRadius: '10px', py: 1.2,
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
                  '&:disabled': { background: '#cbd5e1', color: '#f8fafc' },
                }}
              >
                {isScheduling ? 'Scheduling…' : 'Confirm Schedule'}
              </Button>
            </Paper>

            {/* Cancel (Save Script Only) */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="text"
                startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                onClick={handleSaveScriptOnly}
                sx={{
                  color: '#64748b', textTransform: 'none', fontWeight: 600, fontSize: 14, px: 3, py: 1, borderRadius: 8,
                  '&:hover': { color: '#0f172a', background: '#f1f5f9' },
                }}
              >
                Just save script, skip video for now
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, bgcolor: '#f8fafc' }}>
          {/* Empty DialogActions simply to add spacing at the bottom or allow future actions */}
        </DialogActions>
      </Dialog>

      {/* ═══ Video Status Modal ═══ */}
      <Dialog
        open={videoStatusModalOpen}
        onClose={() => setVideoStatusModalOpen(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.4)' } } }}
        PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.3)', backgroundColor: '#f8fafc' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc', py: 2, px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>
            Video Generation Progress
          </Typography>
          <IconButton
            onClick={() => setVideoStatusModalOpen(false)}
            sx={{ color: '#94a3b8', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {videoStatusModalOpen && <VideoGenerationStatus userId={userId} buttonColor={buttonColor} />}
        </DialogContent>
      </Dialog>

      {/* ═══ Caption File Editor Dialog ═══ */}
      <Dialog
        open={captionModal.open}
        onClose={() => setCaptionModal((m) => ({ ...m, open: false }))}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.5)' } } }}
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3,
        }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px', mb: 0.2 }}>
              <SubtitlesIcon sx={{ fontSize: 18, mr: 0.8, verticalAlign: 'middle', color: '#fff' }} />
              Edit Caption File
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}>
              {captionModal.locationName} <span style={{ opacity: 0.5 }}>•</span> <span className="alt-lang-badge" style={{ fontSize: 10, margin: 0 }}>{getLangName(captionModal.lang)}</span>
            </Typography>
          </Box>
          <IconButton onClick={() => setCaptionModal((m) => ({ ...m, open: false }))} sx={{ color: '#64748b', '&:hover': { color: '#f8fafc', background: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          {captionModal.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress size={36} />
              <Typography sx={{ ml: 2, color: '#64748b' }}>Loading caption file...</Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* {captionModal.captionUrl && (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, mb: 1, display: 'block', wordBreak: 'break-all' }}>
                  Source: {captionModal.captionUrl}
                </Typography>
              )} */}
              <textarea
                style={{
                  width: '100%', minHeight: 450, fontSize: 17, padding: '18px 20px', borderRadius: 14,
                  border: '2px solid #cbd5e1', fontFamily: 'monospace',
                  outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box',
                  backgroundColor: '#ffffff', color: '#334155', boxShadow: '0 2px 4px rgba(0,0,0,0.02) inset',
                  transition: 'all 0.25s ease'
                }}
                value={captionModal.content}
                onChange={(e) => setCaptionModal((m) => ({ ...m, content: e.target.value }))}
                placeholder={`Enter caption content (SRT/VTT format)...`}
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02) inset'; }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setCaptionModal((m) => ({ ...m, open: false }))} sx={{ textTransform: 'none', color: '#64748b', px: 2, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCaptionContent}
            disabled={captionModal.saving || captionModal.loading}
            startIcon={captionModal.saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: 'linear-gradient(to right, #3b82f6, #2563eb)', color: '#fff',
              textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, py: 0.8,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
              '&:hover': { background: 'linear-gradient(to right, #2563eb, #1d4ed8)' },
            }}
          >
            {captionModal.saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ═══ Delete Confirmation Dialog ═══ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.5)' } } }}
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>
              Delete Locations
            </Typography>
          </Box>
          <IconButton 
            onClick={() => !deleting && setDeleteConfirmOpen(false)} 
            disabled={deleting}
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 3, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This action cannot be undone. All associated data including videos, QR codes, and category assignments will be removed.
            </Alert>
            <Typography sx={{ fontSize: 15, color: '#334155', lineHeight: 1.6 }}>
              You are about to delete <strong>{selectedLocations.size}</strong> location{selectedLocations.size !== 1 ? 's' : ''}. 
              Are you sure you want to continue?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={deleting}
            sx={{ textTransform: 'none', color: '#64748b', px: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: 'linear-gradient(to right, #ef5350, #d32f2f)', color: '#fff',
              textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, py: 0.8,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              '&:hover': { background: 'linear-gradient(to right, #d32f2f, #c62828)' },
              '&:disabled': { background: '#cbd5e1', color: '#f8fafc' },
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Single Delete Confirmation Dialog ═══ */}
      <Dialog
        open={singleDeleteConfirmOpen}
        onClose={() => !deletingSingle && setSingleDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.5)' } } }}
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>
              Delete Location
            </Typography>
          </Box>
          <IconButton 
            onClick={() => !deletingSingle && setSingleDeleteConfirmOpen(false)} 
            disabled={deletingSingle}
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 3, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This action cannot be undone. All associated data including videos, QR codes, and category assignments will be removed.
            </Alert>
            <Typography sx={{ fontSize: 15, color: '#334155', lineHeight: 1.6 }}>
              You are about to delete location: <strong>{locationToDelete ? getLanguageText(locationToDelete.name, 'en') : ''}</strong>
              <br />
              Are you sure you want to continue?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <Button 
            onClick={() => setSingleDeleteConfirmOpen(false)} 
            disabled={deletingSingle}
            sx={{ textTransform: 'none', color: '#64748b', px: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSingleDelete}
            disabled={deletingSingle}
            startIcon={deletingSingle ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: 'linear-gradient(to right, #ef5350, #d32f2f)', color: '#fff',
              textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, py: 0.8,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              '&:hover': { background: 'linear-gradient(to right, #d32f2f, #c62828)' },
              '&:disabled': { background: '#cbd5e1', color: '#f8fafc' },
            }}
          >
            {deletingSingle ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllLocationsTable;
